import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Trophy, Users, GraduationCap, Star } from "lucide-react";

const achievements = [
  {
    icon: Users,
    value: 2500,
    suffix: "+",
    label: "Students Enrolled",
    description: "From nursery to high school",
  },
  {
    icon: GraduationCap,
    value: 120,
    suffix: "+",
    label: "Qualified Teachers",
    description: "Experienced educators",
  },
  {
    icon: Trophy,
    value: 50,
    suffix: "+",
    label: "Awards & Recognition",
    description: "Academic excellence",
  },
  {
    icon: Star,
    value: 98,
    suffix: "%",
    label: "Pass Rate",
    description: "Board exam results",
  },
];

const CounterAnimation = ({ value, suffix }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      const duration = 2000;
      const steps = 60;
      const increment = value / steps;
      let current = 0;

      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }
  }, [isInView, value]);

  return (
    <span ref={ref}>
      {count}
      {suffix}
    </span>
  );
};

export const AchievementsSection = () => {
  return (
    <section className="py-16 md:py-24 bg-primary" data-testid="achievements-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-secondary font-medium text-sm uppercase tracking-wider">
            Our Achievements
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mt-2 mb-4">
            Numbers That Speak
          </h2>
          <div className="w-16 h-1 bg-secondary mx-auto rounded-full"></div>
          <p className="text-white/80 mt-4 max-w-2xl mx-auto">
            Our commitment to excellence is reflected in the achievements of our 
            students and the trust of thousands of families.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {achievements.map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center"
              data-testid={`achievement-${index}`}
            >
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                <item.icon className="w-8 h-8 text-secondary" />
              </div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                <CounterAnimation value={item.value} suffix={item.suffix} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-1">{item.label}</h3>
              <p className="text-sm text-white/70">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
